import React, { useCallback, useEffect, useMemo, useState } from 'react'
import {
  Box, Button, Card, Checkbox, IconButton, InputAdornment, ListSubheader, MenuItem, Table, TableBody, TableCell,
  TableHead, TablePagination, TableRow, TextField, Typography,
} from '@mui/material'
import { LoadingButton } from '@mui/lab'
import AddIcon from '@mui/icons-material/Add'
import ClearIcon from '@mui/icons-material/Clear'
import DownloadOutlinedIcon from '@mui/icons-material/DownloadOutlined'
import EditOutlinedIcon from '@mui/icons-material/EditOutlined'
import ManageSearchIcon from '@mui/icons-material/ManageSearch'
import SearchIcon from '@mui/icons-material/Search'
import UploadFileOutlinedIcon from '@mui/icons-material/UploadFileOutlined'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { useSnackbar } from 'notistack'
import { useDownloadWithPolicy } from '../../common/DownloadPolicyDialog'
import { saveBlob, filenameFromResponse } from '../freight-profitability/freightPnlUtils'
import EmptyState from '../merchants/shared/EmptyState'
import { ReadinessChip } from '../merchants/shared/StateChip'
import { C, SX, TYPE } from '../merchants/shared/tokens'
import { num } from '../ptl-merchant-setup/constants'
import { Banner } from '../ptl-merchant-setup/components/SetupUi'
import PtlPage from '../ptl-merchant-setup/shell/PtlPage'
import { downloadOdaZones, errorText, fetchOdaBands, fetchOdaCouriers, fetchOdaPincodes } from './ptlOdaApi'
import { ODA_EYEBROW, ODA_FILTERS, ODA_ROUTES, SOURCE, odaTabs } from './constants'
import { OdaPincodeDialog, RegularDialog } from './OdaMarkDialogs'

const PAGE_SIZES = [50, 100, 200]
const PINCODE = /^[1-9]\d{5}$/

/**
 * PTL Operations → ODA Management → ODA Pincodes (PTL_ODA_REWORK_DEV_LOG.md). One courier code's ODA pincodes, read
 * from its own zone map rows (B2BZoneId, cp = prozoId): ODA type (the courier's ODA charge), km band and days set by
 * hand, and the extra EDD days they give (by hand → ODA type → band → fallback). Pincodes are made ODA here one at a
 * time (Add / Edit, audited: ODA history) or many with the ODA zone upload. ?code=, ?filter= and ?pincode= keep the view
 * in the link.
 */
export default function PtlOdaPincodes() {
  const navigate = useNavigate()
  const { enqueueSnackbar } = useSnackbar()
  const { triggerDownload, PolicyDialog } = useDownloadWithPolicy()
  const [params, setParams] = useSearchParams()
  const [couriers, setCouriers] = useState(null)
  const [bands, setBands] = useState(null)
  const [canEdit, setCanEdit] = useState(false)
  const [data, setData] = useState(null)
  const [error, setError] = useState('')
  const [page, setPage] = useState(0)
  const [size, setSize] = useState(PAGE_SIZES[0])
  const [pinInput, setPinInput] = useState(params.get('pincode') || '')
  const [editing, setEditing] = useState(null)
  const [adding, setAdding] = useState(false)
  const [downloading, setDownloading] = useState(false)
  // pincodes to make Regular (one row's button, or the ticked ones)
  const [regular, setRegular] = useState(null)
  // pincodes ticked on this page (Make Regular for many at once); a new page or view starts empty
  const [selected, setSelected] = useState(() => new Set())

  const filter = ODA_FILTERS.includes(params.get('filter')) ? params.get('filter') : 'all'
  const pincode = params.get('pincode') || ''
  const codes = useMemo(() => (couriers || []).flatMap((c) => c.codes.map((code) => ({ code, parent: c.parent }))), [couriers])
  // the courier code of the link, else the one this person used last; none yet: the screen asks to pick one
  const code = useMemo(() => {
    const find = (x) => x && codes.find((c) => c.code === x)
    const hit = find(params.get('code')) || find(lastCode())
    return hit ? hit.code : null
  }, [params, codes])
  const parent = codes.find((c) => c.code === code)?.parent

  const setParam = (k, v) => {
    if (k === 'code') rememberCode(v)
    const next = new URLSearchParams(params)
    if (v && !(k === 'filter' && v === 'all')) next.set(k, v)
    else next.delete(k)
    setParams(next, { replace: true })
    setPage(0)
  }

  useEffect(() => {
    fetchOdaCouriers().then(setCouriers).catch((e) => setError(errorText(e, 'Could not load the couriers')))
    fetchOdaBands().then((r) => { setBands(r.bands); setCanEdit(!!r.canEdit) }).catch((e) => setError(errorText(e, 'Could not load the ODA bands')))
  }, [])

  const load = useCallback(() => {
    if (!code) return
    setError('')
    fetchOdaPincodes({ code, filter, pincode, page, size })
      .then(setData)
      .catch((e) => { setData(null); setError(errorText(e, 'Could not load the ODA pincodes')) })
  }, [code, filter, pincode, page, size])
  useEffect(() => { load() }, [load])
  useEffect(() => { setSelected(new Set()) }, [code, filter, pincode, page, size])

  const doDownload = async () => {
    setDownloading(true)
    try {
      const res = await downloadOdaZones(code)
      saveBlob(res.data, filenameFromResponse(res, `ptl_oda_zone_map_${code}.csv`))
    } catch (e) {
      enqueueSnackbar(errorText(e, 'Download failed'), { variant: 'error' })
    } finally {
      setDownloading(false)
    }
  }

  const onPin = (v) => {
    const t = v.replace(/\D/g, '').slice(0, 6)
    setPinInput(t)
    if (t === '' || PINCODE.test(t)) setParam('pincode', t)
  }

  const kpi = (key, label, value, sub, tone) => ({
    key, label, value: data ? num(value) : '', sub, tone, active: filter === key, onClick: () => setParam('filter', filter === key ? 'all' : key),
  })
  const kpis = [
    kpi('all', 'ODA pincodes', data?.odaPincodes, code ? `ODA type or km band in ${code}` : 'In the courier zone map'),
    kpi('type', 'With an ODA type', data?.withType, 'ODA type days apply'),
    kpi('band', 'Band only', data?.bandOnly, 'No ODA type: the band’s days apply'),
    kpi('both', 'Type and band', data?.typeAndBand, 'Band saved, not applied', data?.typeAndBand ? 'warn' : undefined),
    kpi('hand', 'Days set by hand', data?.daysByHand, 'Win over type and band'),
  ]
  const q = code ? `?code=${encodeURIComponent(code)}` : ''
  const rows = data?.rows || []
  const toggle = (pin) => { const next = new Set(selected); if (next.has(pin)) next.delete(pin); else next.add(pin); setSelected(next) }

  return (
    <PtlPage title='ODA Management' tabTitle='ODA Pincodes' eyebrow={ODA_EYEBROW} tabs={odaTabs()}
      description='ODA pincodes of each courier code, from its own zone map. The ODA type (ODA 1 / 2 / 3) drives the courier’s ODA charge; the extra EDD days come from days set by hand, else the ODA type’s days, else the km band’s days, else the default (ODA Bands). Each row says which applies. The merchant price never changes here.'
      actions={(
        <>
          <LoadingButton variant='outlined' loading={downloading} loadingPosition='start' startIcon={<DownloadOutlinedIcon />} disabled={!code}
            onClick={() => triggerDownload(doDownload, `${code} zone map`)} sx={{ ...SX.ctaGhost, minHeight: 36 }}>
            {downloading ? 'Preparing file…' : 'Download zone map'}
          </LoadingButton>
          <Button variant='outlined' startIcon={<ManageSearchIcon />} disabled={!code} onClick={() => navigate(`${ODA_ROUTES.history}${q}`)} sx={{ ...SX.ctaGhost, minHeight: 36 }}>ODA history</Button>
          {canEdit && (
            <Button variant='outlined' startIcon={<UploadFileOutlinedIcon />} disabled={!code} onClick={() => navigate(`${ODA_ROUTES.upload}${q}`)} sx={{ ...SX.ctaGhost, minHeight: 36, whiteSpace: 'nowrap' }}>
              Upload zone map
            </Button>
          )}
          {canEdit && (
            <Button variant='contained' startIcon={<AddIcon />} disabled={!code || !bands} onClick={() => setAdding(true)} sx={{ ...SX.cta, minHeight: 36, whiteSpace: 'nowrap' }}>
              Add ODA pincode
            </Button>
          )}
        </>
      )}
      kpis={kpis} kpisLoading={!!code && !data && !error} kpisLabel={`${code || 'Courier'} ODA pincodes`}>
      {error && <Banner tone='danger' action={<Button size='small' onClick={load} sx={{ textTransform: 'none' }}>Retry</Button>}>{error}</Banner>}
      {bands && !canEdit && <Banner tone='info'>You can view ODA pincodes. Changing them needs PTL_SERVICEABILITY_ADMIN: ask a Proship admin in User Management.</Banner>}

      <Card elevation={0} sx={{ ...SX.card, overflow: 'hidden' }}>
        <Box sx={{ p: 2, display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1.5, borderBottom: `1px solid ${C.line}`, bgcolor: C.surfaceSunken }}>
          <TextField select size='small' label='Courier code' value={code || ''} onChange={(e) => { setParam('code', e.target.value); setPinInput('') }}
            sx={{ width: { xs: '100%', sm: 300 }, bgcolor: C.surface }} disabled={!couriers}>
            {(couriers || []).flatMap((c) => [
              <ListSubheader key={`h-${c.parent}`} sx={{ ...TYPE.eyebrow, fontSize: 10, lineHeight: '28px', color: C.inkFaint }}>{c.parent}</ListSubheader>,
              ...c.codes.map((x) => <MenuItem key={x} value={x}>{x}</MenuItem>),
            ])}
          </TextField>
          <TextField size='small' placeholder='Find a pincode' value={pinInput} onChange={(e) => onPin(e.target.value)}
            sx={{ width: { xs: '100%', sm: 220 }, bgcolor: C.surface }} inputProps={{ 'aria-label': 'Find a pincode', inputMode: 'numeric' }}
            InputProps={{
              startAdornment: <InputAdornment position='start'><SearchIcon sx={{ fontSize: 18, color: C.inkGhost }} /></InputAdornment>,
              endAdornment: pinInput ? (
                <InputAdornment position='end'>
                  <IconButton size='small' aria-label='Clear pincode' edge='end' onClick={() => onPin('')}><ClearIcon fontSize='small' /></IconButton>
                </InputAdornment>
              ) : null,
            }} />
          <Typography role='status' sx={{ ...TYPE.rowHelp, color: C.inkMuted }}>
            {data ? `${num(data.total)} pincode${data.total === 1 ? '' : 's'}${filter !== 'all' ? ' in this view' : ''}${parent ? ` · ${parent}` : ''}` : code ? 'Loading pincodes…' : ''}
          </Typography>
        </Box>

        {canEdit && selected.size > 0 && (
          <Box role='region' aria-label='Selected pincodes' sx={{ px: 2, py: 1.25, display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1.5, borderBottom: `1px solid ${C.line}`, bgcolor: C.accentSoft }}>
            <Typography role='status' sx={{ ...TYPE.rowLabel }}>{num(selected.size)} pincode{selected.size === 1 ? '' : 's'} selected</Typography>
            <Button size='small' variant='outlined' onClick={() => setRegular(rows.filter((r) => selected.has(r.pincode)))}
              sx={{ ...SX.ctaGhost, fontSize: 12.5, minHeight: 32, color: C.danger, borderColor: C.dangerBorder, bgcolor: C.surface, '&:hover': { color: C.danger, borderColor: C.danger, bgcolor: C.dangerSoft } }}>
              Make Regular ({num(selected.size)})
            </Button>
            <Button size='small' onClick={() => setSelected(new Set())} sx={{ textTransform: 'none', fontWeight: 600, fontSize: 12.5, color: C.inkMuted }}>Clear selection</Button>
          </Box>
        )}

        {couriers && !code ? (
          <Box sx={{ p: 2 }}>
            <EmptyState dense title='Pick a courier code' body='Choose the courier code whose ODA pincodes you want to see or set. The screen remembers it for next time.' />
          </Box>
        ) : data && rows.length === 0 ? (
          <Box sx={{ p: 2 }}>
            <EmptyState dense title={pincode ? `${pincode} is not ODA for ${code}` : data.odaPincodes ? 'No pincode in this view' : `No ODA pincode for ${code}`}
              body={pincode ? 'Only pincodes with an ODA type or a km band in this code’s zone map are listed. Add it with Add ODA pincode.'
                : data.odaPincodes ? 'Pick another card above to change the view.' : 'Its zone map has no ODA type or km band, so its shipments get no ODA days.'} />
          </Box>
        ) : (
          <Box sx={{ overflowX: 'auto' }}>
            <Table size='small' sx={{ minWidth: 960 }} aria-label={`${code || ''} ODA pincodes`}>
              <TableHead>
                <TableRow>
                  {canEdit && (
                    <TableCell padding='checkbox' sx={{ bgcolor: C.surfaceAlt, borderColor: C.line }}>
                      <Checkbox size='small' inputProps={{ 'aria-label': 'Select all pincodes on this page' }}
                        checked={!!rows.length && rows.every((r) => selected.has(r.pincode))}
                        indeterminate={rows.some((r) => selected.has(r.pincode)) && !rows.every((r) => selected.has(r.pincode))}
                        onChange={(e) => setSelected(e.target.checked ? new Set(rows.map((r) => r.pincode)) : new Set())} />
                    </TableCell>
                  )}
                  {['Pincode', 'City, State', 'Zone', 'ODA type', 'Km band', 'Days by hand', 'Extra EDD days', ''].map((h, i) => (
                    <TableCell key={i} sx={{ ...TYPE.eyebrow, fontSize: 10, color: C.inkFaint, bgcolor: C.surfaceAlt, borderColor: C.line, py: 1.25, whiteSpace: 'nowrap' }}>{h}</TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {!data && <TableRow><TableCell colSpan={canEdit ? 9 : 8} sx={{ ...TYPE.rowHelp, textAlign: 'center', py: 3 }}>{error ? '—' : 'Loading pincodes…'}</TableCell></TableRow>}
                {rows.map((r) => (
                  <TableRow key={r.pincode} selected={selected.has(r.pincode)} sx={{ '& td': { borderColor: C.lineSoft, py: 1 } }}>
                    {canEdit && (
                      <TableCell padding='checkbox'>
                        <Checkbox size='small' checked={selected.has(r.pincode)} inputProps={{ 'aria-label': `Select ${r.pincode}` }} onChange={() => toggle(r.pincode)} />
                      </TableCell>
                    )}
                    <TableCell sx={{ ...TYPE.rowLabel, whiteSpace: 'nowrap' }}>{r.pincode}</TableCell>
                    <TableCell sx={{ fontSize: 12.5, color: C.inkBody }}>{[r.city, r.state].filter(Boolean).join(', ') || '—'}</TableCell>
                    <TableCell sx={{ fontSize: 12.5, color: C.inkBody }}>{r.zone || '—'}</TableCell>
                    <TableCell sx={{ fontSize: 12.5, color: r.odaType ? C.inkBody : C.inkFaint, whiteSpace: 'nowrap' }}>{r.odaType || 'None'}</TableCell>
                    <TableCell sx={{ fontSize: 12.5, color: r.band ? C.inkBody : C.inkFaint }}>
                      {r.band ? r.bandLabel : 'No band'}
                      {r.band && r.odaType && <Typography sx={{ ...TYPE.meta, color: C.inkFaint }}>Not applied: ODA type wins</Typography>}
                    </TableCell>
                    <TableCell sx={{ fontSize: 12.5, color: r.handDays != null ? C.inkBody : C.inkFaint }}>{r.handDays != null ? `+${r.handDays}` : '—'}</TableCell>
                    <TableCell sx={{ minWidth: 220 }}>
                      <Box sx={{ whiteSpace: 'nowrap' }}>
                        <Box component='span' sx={{ ...TYPE.value, fontSize: 13, mr: 1 }}>+{r.days}</Box>
                        <ReadinessChip tone={SOURCE[r.source].tone} label={SOURCE[r.source].label} />
                      </Box>
                      {r.rule && <Typography sx={{ ...TYPE.meta, color: C.inkMuted, mt: 0.25 }}>{r.rule}</Typography>}
                    </TableCell>
                    <TableCell align='right'>
                      {canEdit && (
                        <Box sx={{ display: 'inline-flex', gap: 0.5, whiteSpace: 'nowrap' }}>
                          <Button size='small' variant='outlined' startIcon={<EditOutlinedIcon />} disabled={!bands} onClick={() => setEditing(r)} aria-label={`Edit ODA of ${r.pincode}`}
                            sx={{ ...SX.ctaGhost, fontSize: 12.5, minHeight: 32 }}>
                            Edit
                          </Button>
                          <Button size='small' variant='outlined' onClick={() => setRegular([r])} aria-label={`Make ${r.pincode} Regular`}
                            sx={{
                              ...SX.ctaGhost, fontSize: 12.5, minHeight: 32, color: C.danger, borderColor: C.dangerBorder,
                              '&:hover': { color: C.danger, borderColor: C.danger, bgcolor: C.dangerSoft },
                            }}>
                            Make Regular
                          </Button>
                        </Box>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Box>
        )}
        {data && data.total > 0 && (
          <TablePagination component='div' count={data.total} page={page} onPageChange={(_, p) => setPage(p)} rowsPerPage={size}
            onRowsPerPageChange={(e) => { setSize(Number(e.target.value)); setPage(0) }} rowsPerPageOptions={PAGE_SIZES} sx={{ borderTop: `1px solid ${C.line}` }} />
        )}
      </Card>

      {PolicyDialog}
      {(adding || editing) && bands && (
        <OdaPincodeDialog code={code} bands={bands} row={editing} onClose={() => { setAdding(false); setEditing(null) }}
          onSaved={(msg) => { setAdding(false); setEditing(null); enqueueSnackbar(msg, { variant: 'success' }); load() }} />
      )}
      {regular && (
        <RegularDialog code={code} rows={regular} onClose={() => setRegular(null)}
          onSaved={(msg, variant) => { setRegular(null); setSelected(new Set()); enqueueSnackbar(msg, { variant }); load() }} />
      )}
    </PtlPage>
  )
}

const LAST_CODE = 'ptlOda.lastCode'

/** The courier code this browser last picked on ODA Pincodes (a convenience only; storage may be off). */
function lastCode() {
  try {
    return window.localStorage.getItem(LAST_CODE)
  } catch (e) {
    return null
  }
}

function rememberCode(code) {
  try {
    if (code) window.localStorage.setItem(LAST_CODE, code)
  } catch (e) {
    // storage off (private window): the screen just asks again next time
  }
}
